import React, { useEffect, useId, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import axios from 'axios';
import { useQueryClient } from '@tanstack/react-query';
import { MarkButton, MarkInput, MarkLabel } from './MarkControls';
import QueryInlineError from '../../../components/QueryInlineError';
import useCreateMarkTemplate from '../api/useCreateMarkTemplate';
import useUpdateMarkTemplate from '../api/useUpdateMarkTemplate';
import useTemplatePreview from '../api/useTemplatePreview';
import useTemplateEditorContext from '../api/useTemplateEditorContext';
import { templateKeys, blastKeys, dripKeys } from '../api/queryKeys';
import useDebouncedValue from './BlockEditor/useDebouncedValue';
import useUnsavedChangesRegistration from '../hooks/useUnsavedChangesRegistration';
import type {
    MarkTemplateRow,
    PreviewRequest,
    PreviewResponse,
    TemplateTargetKeys
} from '../types';
import { extractBlastErrorMessage } from '../pages/Blasts/blastFormUtils';
import * as styles from './htmlTemplateEditor.module.css';
import * as layout from './markLayout.module.css';
import { useAsyncLifetime } from './TemplateLibraryPicker';
import EmailPreviewFrame from './EmailPreviewFrame';

type Draft = Pick<MarkTemplateRow, 'subject' | 'preheader'> & { name: string; html: string };
type Props = {
    initial?: Partial<Draft>;
    templateId?: number | null;
    target?: TemplateTargetKeys;
    targetKind?: string | null;
    readOnly?: boolean;
    initialDirty?: boolean;
    guardToken?: string;
    onSaved?: (id: number, row: MarkTemplateRow) => void;
};

const errorMessage = (error: unknown): string =>
    axios.isAxiosError(error)
        ? extractBlastErrorMessage(error)
        : 'The request failed. Please retry.';

const STARTER_HTML = `<body style="margin:0;padding:32px;font-family:Arial,sans-serif;color:#333333;background:#ffffff;">
<h1 style="font-size:28px;line-height:1.3;">Your headline</h1>
<p style="font-size:16px;line-height:1.6;">Hi {{FIRST_NAME}},</p>
<p style="font-size:16px;line-height:1.6;">Write your message here.</p>
<p style="font-size:12px;margin-top:32px;"><a href="{{UNSUBSCRIBE_URL}}">Unsubscribe</a></p>
</body>`;

const examples = [
    '<a href="{{UNSUBSCRIBE_URL}}">Unsubscribe</a>',
    '<p>Hi {{FIRST_NAME}}</p>',
    '{{#if FIRST_NAME}}<p>Hi {{FIRST_NAME}}</p>{{else}}<p>Hi</p>{{/if}}'
];

const HtmlTemplateEditor: React.FC<Props> = ({
    initial,
    templateId = null,
    target,
    targetKind,
    readOnly = false,
    initialDirty = false,
    guardToken,
    onSaved
}) => {
    const [draft, setDraft] = useState<Draft>({
        name: initial?.name ?? '',
        subject: initial?.subject ?? '',
        preheader: initial?.preheader ?? '',
        html: initial?.html ?? ''
    });
    const [id, setId] = useState(templateId);
    const [dirty, setDirty] = useState(initialDirty);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [rendered, setRendered] = useState<{ key: string; data: PreviewResponse } | null>(null);
    const [previewError, setPreviewError] = useState<{ key: string; message: string } | null>(null);
    const [device, setDevice] = useState<'desktop' | 'mobile'>('desktop');
    const [previewExpanded, setPreviewExpanded] = useState(false);
    const [settingsOpen, setSettingsOpen] = useState(
        !initial?.subject?.trim() || (!target && !initial?.name?.trim())
    );
    const [variableSearch, setVariableSearch] = useState('');
    const [insertionField, setInsertionField] = useState<string | null>(null);
    const [copyFeedback, setCopyFeedback] = useState('');
    const [savedMessage, setSavedMessage] = useState(false);
    const [previewAttempt, setPreviewAttempt] = useState(0);
    const errorRef = useRef<HTMLDivElement>(null);
    const sourceRef = useRef<HTMLTextAreaElement | null>(null);
    const lineNumbersRef = useRef<HTMLDivElement>(null);
    const tabExitRef = useRef(false);
    const sourceInputHandledRef = useRef(false);
    const fieldPrefix = useId();
    const insertion = useRef<{
        key: Exclude<keyof Draft, 'name'>;
        element: HTMLInputElement | HTMLTextAreaElement;
    } | null>(null);
    const queryClient = useQueryClient();
    const create = useCreateMarkTemplate();
    const update = useUpdateMarkTemplate();
    const preview = useTemplatePreview();
    const context = useTemplateEditorContext(
        target ? { ...target, template_format: 'html' } : { template_format: 'html' }
    );
    const token =
        guardToken ?? (target ? JSON.stringify(target) : `template-library-${fieldPrefix}`);
    const lifetime = useAsyncLifetime();
    const saveSequence = useRef(0);
    const onSavedRef = useRef(onSaved);
    onSavedRef.current = onSaved;
    const unregister = useUnsavedChangesRegistration(token, dirty, saving);
    // Debounce the serialized body: callers may pass a new `target` object on every render.
    const draftKey = JSON.stringify({
        html: draft.html,
        subject: draft.subject,
        preheader: draft.preheader,
        ...(target ?? {})
    });
    const debouncedKey = useDebouncedValue(draftKey, 400);
    const { mutateAsync: renderPreview } = preview;
    useEffect(() => {
        let current = true;
        const body = JSON.parse(debouncedKey) as PreviewRequest;
        if (!body.html?.trim()) return;
        void renderPreview(body)
            .then((response) => {
                if (current) {
                    setRendered({ key: debouncedKey, data: response.data });
                    setPreviewError(null);
                }
            })
            .catch((cause: unknown) => {
                if (current) setPreviewError({ key: debouncedKey, message: errorMessage(cause) });
            });
        return () => {
            current = false;
        };
    }, [debouncedKey, renderPreview, previewAttempt]);

    const change = (key: keyof Draft, value: string) => {
        setDraft((previous) => ({ ...previous, [key]: value }));
        setDirty(true);
        setSavedMessage(false);
        setError('');
    };
    const syncSourceScroll = () => {
        if (sourceRef.current && lineNumbersRef.current) {
            lineNumbersRef.current.scrollTop = sourceRef.current.scrollTop;
        }
    };
    const handleSourceKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if (readOnly || saving || event.currentTarget.disabled || event.nativeEvent.isComposing)
            return;
        if (event.key === 'Escape') {
            // Consume Escape before the modal's native listener can close the editor.
            event.preventDefault();
            event.stopPropagation();
            tabExitRef.current = true;
            return;
        }
        if (event.key !== 'Tab') {
            tabExitRef.current = false;
            return;
        }
        if (
            event.shiftKey ||
            event.altKey ||
            event.ctrlKey ||
            event.metaKey ||
            tabExitRef.current
        ) {
            tabExitRef.current = false;
            return;
        }
        event.preventDefault();
        event.stopPropagation();
        const element = event.currentTarget;
        const previous = element.value;
        const start = element.selectionStart;
        const end = element.selectionEnd;
        let inserted = false;
        sourceInputHandledRef.current = false;
        try {
            // Native insertion keeps the browser's undo history when supported.
            inserted =
                typeof document.execCommand === 'function' &&
                document.execCommand('insertText', false, '    ');
        } catch {
            // Browsers without this editing command use range replacement below.
        }
        if (!inserted && element.value === previous) {
            element.setRangeText('    ', start, end, 'end');
            element.dispatchEvent(new Event('input', { bubbles: true }));
        } else if (!sourceInputHandledRef.current) {
            change('html', element.value);
        }
        element.setSelectionRange(start + 4, start + 4);
        requestAnimationFrame(syncSourceScroll);
    };
    const insert = (key: string) => {
        const field = insertion.current;
        if (!field || readOnly || saving) return;
        const start = field.element.selectionStart ?? draft[field.key].length;
        const end = field.element.selectionEnd ?? start;
        const value = `{{${key}}}`;
        flushSync(() => {
            if (field.key === 'html') setPreviewExpanded(false);
            else setSettingsOpen(true);
        });
        change(field.key, draft[field.key].slice(0, start) + value + draft[field.key].slice(end));
        const epoch = lifetime.current;
        requestAnimationFrame(() => {
            if (lifetime.current !== epoch || !field.element.isConnected) return;
            field.element.focus();
            field.element.setSelectionRange(start + value.length, start + value.length);
            if (field.key === 'html') syncSourceScroll();
        });
    };
    const copyExample = async (example: string) => {
        const epoch = lifetime.current;
        try {
            await navigator.clipboard.writeText(example);
            if (lifetime.current === epoch)
                setCopyFeedback('Copied. Paste it into your email source.');
        } catch {
            if (lifetime.current === epoch)
                setCopyFeedback('Copy is unavailable. Select the example and copy it manually.');
        }
    };
    const save = async (event: React.FormEvent) => {
        event.preventDefault();
        if (saving || readOnly) return;
        const epoch = lifetime.current;
        const request = ++saveSequence.current;
        const current = () => lifetime.current === epoch && saveSequence.current === request;
        setSaving(true);
        setError('');
        try {
            const content = { ...draft, format: 'html' as const };
            const response =
                id == null
                    ? await create.mutateAsync({ ...content, ...(target ?? {}), channel: 'email' })
                    : await update.mutateAsync({ id, ...content });
            const invalidations = [queryClient.invalidateQueries({ queryKey: templateKeys.all })];
            if (target?.target_type === 'blast_variant') {
                invalidations.push(
                    queryClient.invalidateQueries({ queryKey: blastKeys.detail(target.blast_id) }),
                    queryClient.invalidateQueries({ queryKey: blastKeys.validate(target.blast_id) })
                );
            }
            if (target?.target_type === 'drip_step') {
                invalidations.push(
                    queryClient.invalidateQueries({
                        queryKey: dripKeys.detailRoot(target.drip_id)
                    }),
                    queryClient.invalidateQueries({ queryKey: dripKeys.validate(target.drip_id) })
                );
            }
            await Promise.all(invalidations);
            if (!current()) return;
            setId(response.data.id);
            setSavedMessage(true);
            // Commit the guard cleanup before onSaved navigates, or the blocker still sees a dirty draft.
            flushSync(() => {
                setDirty(false);
                unregister();
            });
            onSavedRef.current?.(response.data.id, response.data);
        } catch (cause: unknown) {
            if (current()) {
                setError(errorMessage(cause));
                requestAnimationFrame(() => errorRef.current?.focus());
            }
        } finally {
            if (current()) setSaving(false);
        }
    };
    const keys = [
        ...(context.data?.data.variables
            .filter(
                (variable) =>
                    !variable.requires_target_kinds?.length ||
                    targetKind === undefined ||
                    variable.requires_target_kinds.includes(targetKind ?? '')
            )
            .map((variable) => variable.key) ?? []),
        ...(context.data?.data.url_slugs ?? [])
    ];
    // Keep the last render on screen while a newer one loads, so typing does not blank it.
    const previewData = rendered?.data ?? null;
    const previewStale = rendered?.key !== draftKey;
    const currentPreviewError = previewError?.key === draftKey ? previewError.message : '';
    const filteredKeys = keys.filter((key) =>
        key.toLowerCase().includes(variableSearch.toLowerCase())
    );
    return (
        <form
            onSubmit={save}
            className={styles.editor}
            onInvalidCapture={(event) => {
                if (event.target instanceof HTMLInputElement) {
                    flushSync(() => setSettingsOpen(true));
                }
            }}
        >
            <details
                className={styles.settings}
                open={settingsOpen}
                onToggle={(event) => setSettingsOpen(event.currentTarget.open)}
            >
                <summary>
                    <strong>Email settings</strong>
                    <span className={styles.settingsSummary}>
                        {(!previewStale && previewData?.rendered_subject) ||
                            draft.subject ||
                            'Add a subject'}
                    </span>
                </summary>
                <fieldset
                    disabled={saving || readOnly}
                    className={`${styles.fields} ${!target ? styles.libraryFields : ''}`}
                >
                    {!target && (
                        <div className={styles.field}>
                            <MarkLabel htmlFor={`${fieldPrefix}-name`}>Template name</MarkLabel>
                            <MarkInput
                                compact
                                id={`${fieldPrefix}-name`}
                                value={draft.name}
                                maxLength={255}
                                required
                                placeholder="Internal name"
                                onFocus={() => {
                                    insertion.current = null;
                                    setInsertionField(null);
                                }}
                                onChange={(event) => change('name', event.currentTarget.value)}
                            />
                        </div>
                    )}
                    {(['subject', 'preheader'] as const).map((key) => (
                        <div key={key} className={styles.field}>
                            <MarkLabel htmlFor={`${fieldPrefix}-${key}`}>
                                {key === 'subject' ? 'Subject' : 'Preview text (optional)'}
                            </MarkLabel>
                            <MarkInput
                                compact
                                id={`${fieldPrefix}-${key}`}
                                value={draft[key]}
                                maxLength={255}
                                required={key === 'subject'}
                                placeholder={
                                    key === 'subject' ? 'Email subject' : 'Inbox preview text'
                                }
                                onFocus={(event) => {
                                    insertion.current = { key, element: event.currentTarget };
                                    setInsertionField(
                                        key === 'subject' ? 'Subject' : 'Preview text'
                                    );
                                }}
                                aria-describedby={
                                    key === 'preheader'
                                        ? `${fieldPrefix}-preheader-hint`
                                        : undefined
                                }
                                onChange={(event) => change(key, event.currentTarget.value)}
                            />
                            {key === 'preheader' && (
                                <p id={`${fieldPrefix}-preheader-hint`} className={styles.hint}>
                                    Leave empty if your HTML already has a hidden preheader.
                                </p>
                            )}
                        </div>
                    ))}
                </fieldset>
            </details>
            {error && (
                <div role="alert" tabIndex={-1} ref={errorRef} className={styles.error}>
                    <strong>Template could not be saved</strong>
                    <p>{error}</p>
                    <p>Your edits are still here.</p>
                </div>
            )}
            <div className={`${styles.split} ${previewExpanded ? styles.expanded : ''}`}>
                <section className={styles.source} hidden={previewExpanded}>
                    <header className={styles.panelHeader}>
                        <MarkLabel htmlFor={`${fieldPrefix}-html`}>HTML source</MarkLabel>
                        <span className={styles.hint}>Paste your email HTML</span>
                    </header>
                    {!readOnly && !draft.html.trim() && (
                        <MarkButton
                            compact
                            disabled={saving}
                            onClick={() => change('html', STARTER_HTML)}
                        >
                            Start with simple HTML
                        </MarkButton>
                    )}
                    <div className={styles.codeEditor}>
                        <div ref={lineNumbersRef} className={styles.lineNumbers} aria-hidden="true">
                            {draft.html
                                .split('\n')
                                .map((_, index) => index + 1)
                                .join('\n')}
                        </div>
                        <textarea
                            ref={(element) => {
                                sourceRef.current = element;
                                syncSourceScroll();
                            }}
                            id={`${fieldPrefix}-html`}
                            className={styles.code}
                            value={draft.html}
                            required
                            disabled={saving || readOnly}
                            placeholder={'<p>Your email content goes here.</p>'}
                            spellCheck={false}
                            wrap="off"
                            aria-describedby={
                                saving || readOnly ? undefined : `${fieldPrefix}-html-hint`
                            }
                            onFocus={(event) => {
                                tabExitRef.current = false;
                                insertion.current = { key: 'html', element: event.currentTarget };
                                setInsertionField('HTML source');
                            }}
                            onBlur={() => {
                                tabExitRef.current = false;
                            }}
                            onKeyDownCapture={handleSourceKeyDown}
                            onScroll={syncSourceScroll}
                            onChange={(event) => {
                                sourceInputHandledRef.current = true;
                                tabExitRef.current = false;
                                change('html', event.currentTarget.value);
                            }}
                        />
                    </div>
                    {!saving && !readOnly && (
                        <p id={`${fieldPrefix}-html-hint`} className={styles.hint}>
                            Tab inserts 4 spaces. Press Escape, then Tab to move to the next
                            control.
                        </p>
                    )}
                </section>
                <section className={styles.previewPanel} aria-label="Live HTML preview">
                    <header className={styles.panelHeader}>
                        <strong>Email preview</strong>
                        <div role="group" aria-label="Preview width" className={styles.actions}>
                            <MarkButton
                                compact
                                aria-pressed={device === 'desktop'}
                                onClick={() => setDevice('desktop')}
                            >
                                Desktop
                            </MarkButton>
                            <MarkButton
                                compact
                                aria-pressed={device === 'mobile'}
                                onClick={() => setDevice('mobile')}
                            >
                                Mobile
                            </MarkButton>
                        </div>
                        <MarkButton
                            compact
                            aria-pressed={previewExpanded}
                            disabled={!draft.html.trim()}
                            onClick={() => setPreviewExpanded(!previewExpanded)}
                        >
                            {previewExpanded ? 'Show HTML & preview' : 'Expand preview'}
                        </MarkButton>
                    </header>
                    <div className={styles.previewCanvas}>
                        {currentPreviewError ? (
                            <div role="alert" className={styles.error}>
                                <strong>Preview needs attention</strong>
                                <p>{currentPreviewError}</p>
                                <MarkButton
                                    compact
                                    onClick={() => setPreviewAttempt((attempt) => attempt + 1)}
                                >
                                    Retry preview
                                </MarkButton>
                            </div>
                        ) : !draft.html.trim() ? (
                            <div className={styles.previewEmpty}>
                                <strong>Your email will appear here</strong>
                                <p>Paste HTML on the left to see it with sample personalization.</p>
                            </div>
                        ) : !previewData ? (
                            <p role="status">Updating preview…</p>
                        ) : (
                            <>
                                {previewStale && (
                                    <p role="status" className={layout.srOnly}>
                                        Updating preview…
                                    </p>
                                )}
                                <EmailPreviewFrame
                                    title="Template preview"
                                    html={previewData.html}
                                    device={device}
                                    height={640}
                                />
                            </>
                        )}
                    </div>
                    <p className={styles.hint}>
                        Preview uses sample values. Saving does not send this email.
                    </p>
                </section>
            </div>
            {!readOnly && (
                <details className={styles.tools}>
                    <summary>Unsubscribe link &amp; HTML help</summary>
                    <aside className={styles.guidance} aria-label="Unsubscribe guidance">
                        <p className={styles.hint}>
                            Static CSS and media queries are supported. Scripts and HTML comments
                            are not supported; keep personalization outside style tags.
                        </p>
                        <strong>Sending a marketing email?</strong>
                        <p>
                            Keep this link in the email body, outside any {'{{#if}}'} blocks. A
                            regular website link does not replace the recipient’s unsubscribe link.
                        </p>
                        <div className={styles.example}>
                            <code>{examples[0]}</code>
                            <MarkButton compact onClick={() => void copyExample(examples[0])}>
                                Copy unsubscribe link
                            </MarkButton>
                        </div>
                        <p role="status" className={styles.hint}>
                            {copyFeedback}
                        </p>
                    </aside>
                </details>
            )}
            {!readOnly && (
                <details className={styles.tools}>
                    <summary>Personalization &amp; links</summary>
                    <aside aria-label="Template variables">
                        <p className={styles.hint}>
                            Place your cursor in Subject, Preview text, or HTML source, then choose
                            a variable to insert it.
                        </p>
                        {context.error && <QueryInlineError error={context.error} />}
                        {context.isLoading && <p role="status">Loading personalization…</p>}
                        <div className={styles.field}>
                            <MarkLabel htmlFor={`${fieldPrefix}-variables`}>
                                Find a variable or link
                            </MarkLabel>
                            <MarkInput
                                compact
                                id={`${fieldPrefix}-variables`}
                                type="search"
                                value={variableSearch}
                                onChange={(event) => setVariableSearch(event.currentTarget.value)}
                            />
                        </div>
                        <p role="status" className={styles.hint}>
                            {insertionField
                                ? `Insert into ${insertionField}`
                                : 'Choose a field in your email first.'}
                        </p>
                        <div className={styles.variables}>
                            {filteredKeys.map((key) => (
                                <MarkButton
                                    compact
                                    key={key}
                                    disabled={saving || !insertionField}
                                    onMouseDown={(event) => event.preventDefault()}
                                    onClick={() => insert(key)}
                                >{`{{${key}}}`}</MarkButton>
                            ))}
                        </div>
                        {!context.isLoading && !context.error && filteredKeys.length === 0 && (
                            <p>No matching variables or links.</p>
                        )}
                        <details>
                            <summary>Personalization examples</summary>
                            {examples.slice(1).map((example) => (
                                <div key={example} className={styles.example}>
                                    <code>{example}</code>
                                    <MarkButton compact onClick={() => void copyExample(example)}>
                                        Copy example
                                    </MarkButton>
                                </div>
                            ))}
                        </details>
                    </aside>
                </details>
            )}
            {!readOnly && (
                <footer className={styles.footer}>
                    <div aria-live="polite">
                        <strong>
                            {saving
                                ? 'Saving…'
                                : dirty
                                  ? 'Unsaved changes'
                                  : savedMessage
                                    ? 'Changes saved'
                                    : 'No unsaved changes'}
                        </strong>
                    </div>
                    <MarkButton compact type="submit" variant="primary" disabled={saving || !dirty}>
                        {saving ? 'Saving…' : 'Save template'}
                    </MarkButton>
                </footer>
            )}
        </form>
    );
};

export default HtmlTemplateEditor;
